// src/utils/paymentUtils.js
import { doc, getDoc } from "firebase/firestore";
import { db } from "../config/firebaseConfig";
import { billingGetStatus } from "./billingCallables";
import { sanitizeLogData } from "./logSanitizer";

// Enhanced logging
const paymentLog = (level, message, data = {}) => {
  const timestamp = new Date().toISOString();
  const logData = sanitizeLogData({ ...data, timestamp });
  
  if (level === 'error') {
    console.error(`❌ [paymentUtils] ${message}`, logData);
  } else if (level === 'warn') {
    console.warn(`⚠️ [paymentUtils] ${message}`, logData);
  } else if (level === 'info') {
    console.log(`ℹ️ [paymentUtils] ${message}`, logData);
  } else {
    console.log(`[paymentUtils] ${message}`, logData);
  }
};

const SUBSCRIPTION_SUCCESS_STATUSES = new Set(['trialing', 'active', 'past_due']);
const normalizeStatus = (value) => (value ? String(value).toLowerCase() : '');

export const isSuccessfulSubscriptionStatus = (status) => SUBSCRIPTION_SUCCESS_STATUSES.has(normalizeStatus(status));

export const fetchSubscriptionSnapshot = async (customerId) => {
  paymentLog('warn', 'fetchSubscriptionSnapshot is deprecated; canonical billing status is uid-scoped', {
    hasCustomerId: Boolean(customerId),
  });
  return null;
};

// Normalize various possible shapes for the payments doc
export const normalizePaymentDoc = (data) => {
  if (data == null) {
    return null;
  }
  if (typeof data !== 'object') {
    paymentLog('warn', 'Normalize received invalid data', { data });
    return data;
  }

  const out = { ...data };

  // Derive a unified customerId from multiple possible fields
  const candidates = [
    data.customerId,
    data.stripeCustomerId,
    (data.customer && typeof data.customer === 'string') ? data.customer : null,
    (data.customer && typeof data.customer === 'object') ? data.customer.id : null,
    (data.stripe && typeof data.stripe === 'object') ? (data.stripe.customerId || data.stripe.customer?.id) : null,
    data.customer_id,
    data.stripe_customer_id,
  ].filter(Boolean);
  
  const firstId = candidates.find((v) => typeof v === 'string' && v.trim());
  
  if (firstId) {
    out.customerId = firstId;
    if (!out.stripeCustomerId) out.stripeCustomerId = firstId;
    if (!out.customer) out.customer = { id: firstId };
    
    paymentLog('info', 'Normalized customer ID', {
      hadOriginalCustomerId: Boolean(data.customerId || data.stripeCustomerId),
      hasNormalizedCustomerId: Boolean(firstId),
    });
  }

  // Normalize boolean paid flag
  if (out.isPaid == null && typeof out.paid === 'boolean') out.isPaid = !!out.paid;
  if (out.paid == null && typeof out.isPaid === 'boolean') out.paid = !!out.isPaid;

  // Ensure timestamp is a proper date if it exists
  if (out.timestamp && typeof out.timestamp.toDate === 'function') {
    out.timestamp = out.timestamp.toDate();
  }

  return out;
};

/**
 * Fetches a payment document from Firestore.
 * @param {string} userId
 * @returns {Promise<object|null>}
 */
const fetchPaymentDoc = async (userId, retailerId = null) => {
  if (!userId) {
    paymentLog('error', 'Missing user ID for payment fetch');
    throw new Error("User ID is required.");
  }

  const candidates = [
    String(userId || '').trim(),
    retailerId ? String(retailerId).trim() : null,
  ].filter(Boolean);

  try {
    for (const id of candidates) {
      const ref = doc(db, "payments", id);
      const snap = await getDoc(ref);

      if (!snap.exists()) {
        paymentLog('info', 'No payment record found', { lookupId: id, userId, retailerId: retailerId || null });
        continue;
      }

      const raw = snap.data();
      const normalized = normalizePaymentDoc(raw);

      paymentLog('info', 'Payment doc fetched', {
        lookupId: id,
        userId,
        retailerId: retailerId || null,
        exists: true,
        paid: normalized.paid,
        status: normalized.status || null,
        hasCustomerId: !!normalized.customerId
      });

      return normalized;
    }

    return null;
  } catch (err) {
    paymentLog('error', 'Failed to fetch payment document', {
      userId,
      retailerId: retailerId || null,
      error: err?.message || String(err)
    });
    return null;
  }
};

/**
 * Validates a promo code.
 * @param {string} code
 * @returns {Promise<boolean>}
 */
export const validatePromoCode = async (code) => {
  if (!code) {
    paymentLog('warn', 'Empty promo code validation attempt');
    return false;
  }

  const trimmedCode = code.trim();
  
  try {
    const ref = doc(db, "promo_codes", trimmedCode);
    const snap = await getDoc(ref);
    
    if (!snap.exists()) {
      paymentLog('info', 'Promo code not found', { code: trimmedCode });
      return false;
    }
    
    const promo = snap.data();
    const now = new Date();
    
    // Check expiry
    if (promo.expiry && now > new Date(promo.expiry)) {
      paymentLog('info', 'Promo code expired', { code: trimmedCode, expiry: promo.expiry });
      return false;
    }
    
    // Check active status and usage limits
    const isValid = promo.active && (promo.unlimited || (promo.usesRemaining > 0));
    
    paymentLog('info', 'Promo code validation result', { 
      code: trimmedCode, 
      isValid,
      active: promo.active,
      unlimited: promo.unlimited,
      usesRemaining: promo.usesRemaining
    });
    
    return isValid;
  } catch (err) {
    paymentLog('error', 'Error validating promo code', { 
      code: trimmedCode, 
      error: err.message 
    });
    return false;
  }
};

const fetchCanonicalBillingStatus = async ({ userId, forceRefresh = false } = {}) => {
  if (!userId) return null;
  try {
    return await billingGetStatus({
      uid: userId,
      forceRefresh: Boolean(forceRefresh),
    });
  } catch (error) {
    paymentLog('warn', 'billingGetStatus callable failed', {
      userId,
      error: error?.message || String(error),
    });
    return null;
  }
};

/**
 * Updates a user's payment status.
 * @param {string} userId
 * @param {boolean} status
 * @param {object} [metadata={}]
 * @returns {Promise<boolean>}
 */
export const updatePaymentStatus = async (userId, status, metadata = {}) => {
  if (!userId) {
    paymentLog('error', 'Missing user ID in updatePaymentStatus');
    return false;
  }

  paymentLog('info', 'Updating payment status', { 
    userId, 
    status, 
    metadataKeys: Object.keys(metadata) 
  });

  // Payment authority is server-owned; this helper only asks server for current status.
  if (status === true) {
    const billingStatus = await fetchCanonicalBillingStatus({ userId, forceRefresh: true });
    if (billingStatus?.isPaidEnough === true) {
      paymentLog('info', 'Billing status confirmed by server', {
        userId,
        state: billingStatus.state || null,
      });
      return true;
    }
    paymentLog('warn', 'Server did not confirm paid status', {
      userId,
      state: billingStatus?.state || null,
      metadataKeys: Object.keys(metadata || {}),
    });
    return false;
  }

  paymentLog('warn', 'Payment status mutation rejected: client-side escalation disabled', {
    userId,
    status,
    metadataKeys: Object.keys(metadata || {}),
  });
  return false;
};

const paymentStatusCache = new Map();
const paymentStatusInFlight = new Map();

/**
 * Checks if a user has paid.
 * Falls back to retailer profile signals and heals missing payment docs.
 * @param {string} userId
 * @returns {Promise<boolean>}
 */
export const checkUserPaymentStatus = async (userId, options = {}) => {
  if (!userId) {
    paymentLog('error', 'Missing user ID in checkUserPaymentStatus');
    return false;
  }

  const { forceRemote = false } = options;
  const now = Date.now();
  const cached = paymentStatusCache.get(userId);
  const cacheFreshMs = forceRemote ? 15_000 : 60_000;

  if (cached && now - cached.timestamp < cacheFreshMs) {
    return cached.value;
  }

  if (paymentStatusInFlight.has(userId)) {
    return await paymentStatusInFlight.get(userId);
  }

  const runCheck = (async () => {
    try {
      const canonical = await fetchCanonicalBillingStatus({ userId, forceRefresh: forceRemote });
      const allowed = canonical?.isPaidEnough === true;
      paymentLog('info', 'Canonical billing status evaluated', {
        userId,
        state: canonical?.state || null,
        isPaidEnough: allowed,
        hasCanonicalBillingDoc: canonical?.hasCanonicalBillingDoc === true,
      });
      return allowed;
    } catch (err) {
      paymentLog('error', 'Error checking payment status', {
        userId,
        error: err.message,
      });
      return false;
    } finally {
      paymentStatusInFlight.delete(userId);
    }
  })();

  paymentStatusInFlight.set(userId, runCheck);
  const result = await runCheck;
  paymentStatusCache.set(userId, { value: result, timestamp: now });
  return result;
};

// processPayment() has been removed.
// All payment processing goes through Stripe Checkout via billingCreateCheckoutSession (server-side callable).
// There is no /api/process-payment endpoint in this project.

/**
 * Retrieves full payment history document.
 * @param {string} userId
 * @returns {Promise<object|null>}
 */
export const getPaymentHistory = async (userId) => {
  paymentLog('info', 'Fetching payment history', { userId });
  return await fetchPaymentDoc(userId);
};

/**
 * Alias for checkUserPaymentStatus.
 * @param {string} userId
 * @returns {Promise<boolean>}
 */
export const hasActiveSubscription = async (userId) => {
  const result = await checkUserPaymentStatus(userId);
  paymentLog('info', 'Active subscription check', { userId, hasActiveSubscription: result });
  return result;
};

/**
 * Calculates prorated credit for the unused portion of the current plan.
 * @param {object} currentPlan
 * @param {Date} [now=new Date()]
 * @returns {number}
 */
export const calculateProratedCredit = (currentPlan, now = new Date()) => {
  if (!currentPlan || !currentPlan.validUntil || !currentPlan.monthlyPrice) {
    paymentLog('warn', 'Missing plan data for prorated calculation', { 
      hasPlan: !!currentPlan,
      hasValidUntil: !!currentPlan?.validUntil,
      hasMonthlyPrice: !!currentPlan?.monthlyPrice
    });
    return 0;
  }

  try {
    const validMs =
      typeof currentPlan.validUntil.toMillis === 'function'
        ? currentPlan.validUntil.toMillis()
        : new Date(currentPlan.validUntil).getTime();
        
    const remainingMs = validMs - now.getTime();
    
    if (remainingMs <= 0) {
      paymentLog('info', 'No credit - plan expired or no time remaining');
      return 0;
    }
    
    const days = remainingMs / (1000 * 60 * 60 * 24);
    const credit = (currentPlan.monthlyPrice / 30) * days;
    const roundedCredit = parseFloat(credit.toFixed(2));
    
    paymentLog('info', 'Prorated credit calculated', {
      monthlyPrice: currentPlan.monthlyPrice,
      remainingDays: days.toFixed(2),
      credit: roundedCredit
    });
    
    return roundedCredit;
  } catch (error) {
    paymentLog('error', 'Error calculating prorated credit', { error: error.message });
    return 0;
  }
};

/**
 * Calculates amendment cost between current and new plan price.
 * @param {object} params
 * @param {object} params.currentPlan
 * @param {number} params.newPlanPrice
 * @returns {object}
 */
export const calculateAmendmentCost = ({ currentPlan, newPlanPrice }) => {
  const now = new Date();
  const credit = calculateProratedCredit(currentPlan, now);
  const amountDue = newPlanPrice - credit;
  const result = { 
    amountDue: parseFloat(amountDue.toFixed(2)), 
    credit, 
    effectiveDate: now 
  };
  
  paymentLog('info', 'Amendment cost calculated', result);
  return result;
};

// Export logging function for debugging
export { paymentLog };
